import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import config from "../config";
import "./ImpressionHomesCCM.css";

const CATEGORIES = [
  { key: "community", label: "Community Listing" },
  { key: "floorplan", label: "Floor Plan Listing" },
  { key: "home", label: "Home Listing" },
];


const ImpressionHomesCCM = () => {
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [prompts, setPrompts] = useState({});
  const [activeTab, setActiveTab] = useState("community");
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(true);
  const [isSavingPrompts, setIsSavingPrompts] = useState(false);
  const [promptsSaved, setPromptsSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [jobs, setJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  // Load saved prompts
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const resp = await fetch(
          `${config.api.baseUrl}/impression-homes/ccm-prompts`,
          { credentials: "include" }
        );
        const data = await resp.json();
        if (data.success) {
          setPrompts(data.prompts);
        }
      } catch (err) {
        console.error("Error loading prompts:", err);
      } finally {
        setIsLoadingPrompts(false);
      }
    };
    loadPrompts();
  }, []);

  // Fetch CCM jobs
  const fetchJobs = useCallback(async () => {
    try {
      setIsLoadingJobs(true);
      const resp = await fetch(
        `${config.api.baseUrl}/impression-homes/jobs?type=ccm`,
        { credentials: "include" }
      );
      const data = await resp.json();
      if (data.success) {
        setJobs(data.jobs);
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll while any job is running
  useEffect(() => {
    const hasRunning = jobs.some(
      (j) => j.status === "queued" || j.status === "processing"
    );
    if (!hasRunning) return;
    const interval = setInterval(() => fetchJobs(), 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  const handlePromptChange = (category, value) => {
    setPrompts((prev) => ({ ...prev, [category]: value }));
    setPromptsSaved(false);
  };

  const handleSavePrompts = async () => {
    setIsSavingPrompts(true);
    try {
      const resp = await fetch(
        `${config.api.baseUrl}/impression-homes/ccm-prompts`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompts }),
        }
      );
      const data = await resp.json();
      if (data.success) {
        setPromptsSaved(true);
        setTimeout(() => setPromptsSaved(false), 3000);
      }
    } catch (err) {
      console.error("Error saving prompts:", err);
    } finally {
      setIsSavingPrompts(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!googleSheetUrl.trim()) {
      setError("Please enter a Google Sheet URL.");
      return;
    }
    setIsSubmitting(true);
    try {
      const resp = await fetch(
        `${config.api.baseUrl}/impression-homes/start-ccm`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            google_sheet_url: googleSheetUrl.trim(),
            prompts,
          }),
        }
      );
      const data = await resp.json();
      if (data.success) {
        setGoogleSheetUrl("");
        fetchJobs();
      } else {
        setError(data.error || "Failed to start job");
      }
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      queued: { label: "Queued", cls: "ihccm-badge-queued" },
      processing: { label: "Processing", cls: "ihccm-badge-processing" },
      completed: { label: "Completed", cls: "ihccm-badge-completed" },
      error: { label: "Error", cls: "ihccm-badge-error" },
    };
    const info = map[status] || { label: status, cls: "" };
    return <span className={`ihccm-badge ${info.cls}`}>{info.label}</span>;
  };

  const getProgressText = (job) => {
    if (job.status === "completed") return "Done";
    if (job.status === "error") return job.error || "An error occurred";
    const p = job.progress || {};
    // The backend now sends stage-aware text like "Stage 1/3: Processing communities (3/5)"
    if (p.current_stage) {
      return p.current_stage;
    }
    if (p.total_rows && p.completed_rows !== undefined) {
      return `Processing — ${p.completed_rows}/${p.total_rows} rows`;
    }
    return "Starting...";
  };

  return (
    <Layout>
      <div className="ihccm-container">
        <div className="ihccm-header">
          <h1>CCM Description Generator</h1>
          <p className="ihccm-subtitle">
            Generate AI-powered descriptions for Impression Homes listings.
            Provide a Google Sheet with three tabs:{" "}
            <code>community</code>, <code>floor_plan</code>, and{" "}
            <code>property</code>. AI-generated columns are written back to the
            same sheet.
          </p>
        </div>

        {/* Prompt Editor */}
        <div className="ihccm-section">
          <div className="ihccm-section-header">
            <h2>Category Prompts</h2>
            <button
              className="ihccm-save-btn"
              onClick={handleSavePrompts}
              disabled={isSavingPrompts}
            >
              {isSavingPrompts
                ? "Saving..."
                : promptsSaved
                  ? "Saved!"
                  : "Save Prompts"}
            </button>
          </div>
          <p className="ihccm-section-desc">
            Each category has a production-ready system prompt with voice/tone,
            field definitions, and guardrails. You can customize these or use
            the defaults.
          </p>

          <div className="ihccm-tabs">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                className={`ihccm-tab ${activeTab === cat.key ? "active" : ""}`}
                onClick={() => setActiveTab(cat.key)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {!isLoadingPrompts && (
            <textarea
              className="ihccm-prompt-editor"
              value={prompts[activeTab] || ""}
              onChange={(e) => handlePromptChange(activeTab, e.target.value)}
              placeholder={`Enter the system prompt for ${activeTab} pages...`}
              rows={20}
            />
          )}
          {isLoadingPrompts && (
            <div className="ihccm-prompt-loading">Loading prompts...</div>
          )}
        </div>

        {/* Google Sheet Input */}
        <form className="ihccm-section ihccm-form" onSubmit={handleSubmit}>
          <h2>Generate Descriptions</h2>
          <p className="ihccm-section-desc">
            Paste the URL of your scraped Google Sheet. Each row will be
            processed with the prompt matching its <code>page_type</code>.
          </p>
          <input
            type="text"
            className="ihccm-url-input"
            placeholder="https://docs.google.com/spreadsheets/d/..."
            value={googleSheetUrl}
            onChange={(e) => setGoogleSheetUrl(e.target.value)}
          />
          {error && <div className="ihccm-error">{error}</div>}
          <button
            type="submit"
            className="ihccm-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Starting..." : "Generate Descriptions"}
          </button>
        </form>

        {/* How it works */}
        <div className="ihccm-section ihccm-output-section">
          <h2>How It Works</h2>
          <p className="ihccm-output-desc">
            AI-generated columns are written directly back to your input sheet.
            New columns (e.g. <code>community_overview</code>,{" "}
            <code>plan_description</code>, <code>status</code>) are added
            automatically to each tab. Communities are processed first
            (with web search), then Floor Plans, then Properties.
          </p>
        </div>

        {/* Jobs */}
        <div className="ihccm-section">
          <h2>Jobs</h2>
          {isLoadingJobs && jobs.length === 0 && (
            <p className="ihccm-muted">Loading...</p>
          )}
          {!isLoadingJobs && jobs.length === 0 && (
            <p className="ihccm-muted">
              No CCM jobs yet. Submit a Google Sheet above to get started.
            </p>
          )}
          {jobs.length > 0 && (
            <div className="ihccm-jobs-list">
              {jobs.map((job) => (
                <div key={job.job_id} className="ihccm-job-card">
                  <div className="ihccm-job-top">
                    <span className="ihccm-job-title">
                      {job.google_sheet_url
                        ? "Google Sheet"
                        : job.input_filename || "CCM Job"}
                    </span>
                    {getStatusBadge(job.status)}
                  </div>
                  <div className="ihccm-job-progress">
                    {getProgressText(job)}
                  </div>
                  {job.status === "processing" &&
                    job.progress?.total_rows > 0 && (
                      <div className="ihccm-progress-track">
                        <div
                          className="ihccm-progress-bar"
                          style={{
                            width: `${Math.round(
                              ((job.progress.completed_rows || 0) /
                                job.progress.total_rows) *
                                100
                            )}%`,
                          }}
                        />
                      </div>
                    )}
                  <div className="ihccm-job-meta">
                    <span>
                      {job.rows_total ? `${job.rows_total} rows` : ""}
                    </span>
                    <span>
                      {job.created_at
                        ? new Date(job.created_at).toLocaleString()
                        : ""}
                    </span>
                  </div>
                  {job.status === "completed" && job.output_url && (
                    <a
                      href={job.output_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ihccm-output-link"
                    >
                      Open Updated Sheet →
                    </a>
                  )}
                  {job.status === "completed" && job.error_count > 0 && (
                    <div className="ihccm-warning">
                      {job.error_count} row(s) had errors.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ImpressionHomesCCM;
